import { useState, useCallback, useEffect } from "react";
import { captureException } from "@/lib/sentry";
import { supabase } from "@/lib/supabase";
import { Album, GlobalAlbumDefinition } from "@/types";

const LOCAL_KEY = "lmi_local_collection";

interface StoredCollection {
  albums: Album[];
}

function loadFromStorage(): Album[] {
  try {
    const raw = localStorage.getItem(LOCAL_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as StoredCollection;
    return Array.isArray(parsed.albums) ? parsed.albums : [];
  } catch {
    return [];
  }
}

export function useLocalCollection() {
  const [albums, setAlbums] = useState<Album[]>(loadFromStorage);

  const hasLocalData = albums.length > 0;

  useEffect(() => {
    if (albums.length === 0) {
      localStorage.removeItem(LOCAL_KEY);
    } else {
      localStorage.setItem(LOCAL_KEY, JSON.stringify({ albums }));
    }
  }, [albums]);

  const createAlbum = useCallback(
    (globalAlbumDef: GlobalAlbumDefinition): Album => {
      const newAlbum: Album = {
        id: crypto.randomUUID(),
        globalAlbumId: globalAlbumDef.id,
        createdAt: Date.now(),
        data: {
          albumSize: globalAlbumDef.totalStickers,
          stickers: {},
        },
      };
      setAlbums((prev) => [...prev, newAlbum]);
      return newAlbum;
    },
    [],
  );

  const updateAlbum = useCallback((updated: Album) => {
    setAlbums((prev) => prev.map((a) => (a.id === updated.id ? updated : a)));
  }, []);

  const deleteAlbum = useCallback((albumId: string) => {
    setAlbums((prev) => prev.filter((a) => a.id !== albumId));
  }, []);

  const clearLocalData = useCallback(() => {
    setAlbums([]);
  }, []);

  /**
   * Copies all local albums + stickers to Supabase after the user signs up/in.
   * Clears localStorage on success. Returns true if at least one album migrated.
   */
  const migrateToSupabase = useCallback(
    async (userId: string): Promise<boolean> => {
      // Read directly from storage to avoid stale closure over `albums` state
      const snapshot = loadFromStorage();
      if (snapshot.length === 0) return true;

      let anySuccess = false;

      for (const album of snapshot) {
        const { data: created, error } = await supabase
          .from("user_albums")
          .upsert(
            { user_id: userId, global_album_id: album.globalAlbumId },
            { onConflict: "user_id,global_album_id" },
          )
          .select()
          .single();

        if (error) {
          captureException(new Error(error.message), {
            extra: {
              action: "migrate_local_album",
              code: error.code,
              details: error.details,
              hint: error.hint,
            },
          });
          continue;
        }

        anySuccess = true;

        const entries = Object.entries(album.data.stickers)
          .filter(([, s]) => s.owned)
          .map(([num, s]) => ({
            user_album_id: created.id,
            sticker_number: Number(num),
            quantity: 1 + s.duplicates,
          }));

        if (entries.length > 0) {
          await supabase
            .from("user_stickers")
            .upsert(entries, { onConflict: "user_album_id,sticker_number" });
        }
      }

      if (anySuccess) {
        setAlbums([]);
      }

      return anySuccess;
    },
    [],
  );

  return {
    albums,
    hasLocalData,
    createAlbum,
    updateAlbum,
    deleteAlbum,
    clearLocalData,
    migrateToSupabase,
  };
}
